/**
 * useAsync — Generic hook for async data fetching with loading/error states.
 *
 * Usage:
 *   const { data, loading, error, refetch } = useAsync(
 *     () => api.tasks.getAll(engagementId),
 *     [engagementId]
 *   );
 */

import { useState, useEffect, useCallback, useRef } from 'react';

interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: Error | null;
}

interface UseAsyncReturn<T> extends AsyncState<T> {
  /** Manually re-trigger the async function */
  refetch: () => void;
}

export function useAsync<T>(
  asyncFn: () => Promise<T>,
  deps: React.DependencyList = []
): UseAsyncReturn<T> {
  const [state, setState] = useState<AsyncState<T>>({
    data: null,
    loading: true,
    error: null,
  });

  // Track the latest call to avoid stale updates
  const mountedRef = useRef(true);
  const callIdRef = useRef(0);

  const execute = useCallback(async () => {
    const callId = ++callIdRef.current;
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const result = await asyncFn();
      // Only update if this is still the latest call and component is mounted
      if (mountedRef.current && callId === callIdRef.current) {
        setState({ data: result, loading: false, error: null });
      }
    } catch (err) {
      if (mountedRef.current && callId === callIdRef.current) {
        setState({
          data: null,
          loading: false,
          error: err instanceof Error ? err : new Error(String(err)),
        });
      }
    }
  }, [asyncFn]);

  useEffect(() => {
    mountedRef.current = true;
    const timerId = window.setTimeout(() => {
      execute();
    }, 0);
    return () => {
      window.clearTimeout(timerId);
      mountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [execute, ...deps]);

  return {
    ...state,
    refetch: execute,
  };
}

export default useAsync;